// Canvas surface + dots theming contract.
//
// Static theming is PURE CSS: pages declare `'@dark' / '@light'` blocks
// (compiled by the framework into `[data-theme="…"]`-scoped rules, see
// element/create.js theme-block handling), so surfaces, dots, and fades follow
// `data-theme` with zero JS. The ONLY imperative pieces here are for true user
// state that CSS cannot derive — the per-theme canvas-background cookie the
// Zoombar picker writes, which rides the document-level `--canvas-bg` var.
//
// TWO RULES (both verified live, both break silently):
// 1. Never write a THEMED value (a resolved colour) as an INLINE style on a
//    node — inline beats the `[data-theme]` class rules and freezes the
//    element on one theme. Inline is reserved for the theme-agnostic
//    `background: var(--canvas-bg, var(--stage-bg, var(--color-stage)))`
//    chain — a var() string re-resolves on every theme flip.
// 2. Props inside `'@dark' / '@light'` blocks must be FLAT — a nested
//    `style: {…}` compiles to a bogus `[data-theme] .cls style`
//    child-element selector instead of declarations on the class.

export const CANVAS_BG_VAR = '--canvas-bg'
// ── ONE ground channel (BF-WIDGET-INK-1, 2026-08-16) ────────────────────────
// Every workspace ground — the shell root, the router content host, the home
// page + board, every canvas Stage — resolves through the SAME chain:
//
//   var(--stage-bg, var(--color-stage))
//
// `--color-stage` is the design-system `stage` token (brand/shared
// designSystem/color.js: light `milk`, dark `gray5`) — the theme-adaptive
// house default. `--stage-bg` is the single OVERRIDE var, written on <html>
// by whoever owns a per-workspace ground: the active module's `chrome.stage`
// (workspace/app.js _setStageVar) or a workspace record's
// `settings.designSystem.vars['--stage-bg']` (workspaceConfigs
// _applyDesignSystem). Remove the property and every ground falls back to the
// token — no per-surface state anywhere.
//
// Why one chain: before this the shell root read `--module-stage-bg`, the
// home board read `--home-bg` → token, the `main` page painted its own
// gray15/gray5, and canvas Stages read `--canvas-bg` → `--canvas-surface-bg`
// (a var nothing wrote any more) → token. Four resolution paths cannot agree
// by tuning any one of them: with bellforge active the root painted its beige
// chrome.stage while the board beside it stayed milk — the seam Nika reported
// ("keep workspace background consistent towards root bg").
//
// `--canvas-bg` stays ABOVE the chain on canvas surfaces only: it is the
// user's own Zoombar pick (per-theme cookie) or a workspace record's
// `canvasBg`, and a user pick must beat the workspace default.
//
// Never define `--stage-bg` at :root with a value (or via @property with an
// initial-value): a defined var never falls back, and the token default is
// the fallback. Overrides SET it, resets REMOVE it.
export const STAGE_BG_VAR = '--stage-bg'
export const STAGE_BACKGROUND = 'var(--stage-bg, var(--color-stage))'
export const CANVAS_SURFACE_BACKGROUND =
  'var(--canvas-bg, var(--stage-bg, var(--color-stage)))'
// Legacy name — no writer since the stage token landed (2026-08-15); kept only
// so external readers of the constant keep resolving. New code reads
// STAGE_BG_VAR.
export const CANVAS_SURFACE_BG_VAR = '--canvas-surface-bg'
export const CANVAS_SURFACE_DEFAULTS = {
  light: 'var(--color-milk)',
  dark: '#0a0a0a'
}

// Dark canvas dots match the home dashboard's lavender grid (per the
// add-app comment: "Same lavender dot grid as the home dashboard + /files").
// Previously the dark dots were a dim white (rgba(255,255,255,0.08)), which
// read visibly different from / — the canvas surfaces (/tickets, /files,
// /studio, /team) now share the home dot grid in dark mode.
// Dot radius rides `--canvas-dot-r` so ZOOMING surfaces can keep the grid's
// ink density constant (see canvasDotMetrics below). The fallback is the
// static value every non-zooming surface (/tickets, /files, /studio, home)
// keeps — finer grain reads as texture rather than a visible pattern
// (2026-08-08 per Nika "make them smaller", then halved again on "dots are
// too big - reduce them by 2": 0.6px → 0.3px, matching the halved divisor).
export const CANVAS_DOTS_DARK =
  'radial-gradient(circle, rgba(215,215,238,0.35) var(--canvas-dot-r, 0.3px), transparent var(--canvas-dot-r, 0.3px))'
export const CANVAS_DOTS_LIGHT =
  'radial-gradient(circle, rgba(215,215,238,0.35) var(--canvas-dot-r, 0.3px), transparent var(--canvas-dot-r, 0.3px))'

// ── Dot-grid metrics — ONE invariant for every zooming canvas surface ───────
//
// The grid must read as the SAME texture at every zoom level. Two things have
// to move together for that:
//   1. SPACING folds into a bounded band, so it never collapses into noise
//      when you zoom out nor stretches into stripes when you zoom in
//      (the recursive/fractal grid).
//   2. The dot RADIUS tracks that spacing, so ink-per-area stays constant.
//
// Pinning the radius while spacing sweeps is what made the grid visibly fade
// out and pop back mid-gesture — measured 2026-08-08 on natali/shop, ink swung
// 7.8x across ONE fold cycle (0.785% of a tile at 12px spacing → 0.100% at
// 33.6px) with the dot stuck at 0.6px. That reads exactly as Nika's report:
// "sometimes during scroll dots disappear".
export const CANVAS_DOT_RADIUS_VAR = '--canvas-dot-r'
export const CANVAS_DOT_BASE_SPACING = 12
export const CANVAS_DOT_FOLD_PERIOD = 3
// radius = spacing / divisor. 40 (was 20) — halved 2026-08-08 per Nika "dots
// are too big - reduce them by 2": a 0.3px dot on 12px spacing at the tight
// end, ~0.62px at a typical 25px board spacing. Halving HERE rather than at
// the call sites keeps ink density constant across the fold (the invariant
// above), so the grid still never fades mid-zoom — every dot just gets
// uniformly finer.
export const CANVAS_DOT_INK_DIVISOR = 40

// Rendering-visibility floor, in CSS px — DELIBERATELY separate from the
// ink-density formula above. QA (CODEX-V8 row 3, 2026-08-13) found the
// panzoom boards' own DEFAULT_BASE_DOT=24 (canvas/functions/
// initPanzoomBoard.js — a base-spacing constant independent of
// CANVAS_DOT_BASE_SPACING here) combined with CANVAS_DOT_INK_DIVISOR=40
// yields a mathematically-correct-but-invisible 24/40=0.6px radius at
// scale 1 (this Chrome build's empirical visibility floor is ~0.8-1px; a
// same-crop-region A/B/C screenshot compare found 0.6px produced ZERO
// visible dots while 1px was barely visible and 4px was clearly visible).
// The ink-density ratio invariant (`canvasDotMetrics` above,
// `canvasDotMetrics.test.js`) must stay mathematically pure — it is a
// separate, already-locked contract — so this floor is applied ONLY at the
// `applyCanvasDotMetrics` call site via the opt-in `opts.minRadius`, never
// inside the pure formula. Callers whose base/divisor pairing can reach a
// sub-visibility radius (every board that boots through
// canvas/functions/initPanzoomBoard.js) pass `minRadius:
// CANVAS_DOT_VISIBILITY_FLOOR`; callers that don't opt in are unaffected
// (existing canvasDotMetrics.test.js assertions on the unclamped 0.3px/
// 0.6px tight-end values stay byte-identical).
export const CANVAS_DOT_VISIBILITY_FLOOR = 1

// Pure: camera scale → { spacing, radius } in CSS px. Guards every degenerate
// scale (0, negative, NaN, Infinity) back to `base` rather than emitting a
// NaN/0 background-size, which would blank the grid outright.
// Defaults are the literal values of CANVAS_DOT_BASE_SPACING/
// CANVAS_DOT_FOLD_PERIOD above, inlined rather than referenced — frank's
// `to-json` free-var resolver doesn't walk into a positional default-
// parameter expression the way it does an object-destructuring default
// (`{ x = MODULE_CONST } = {}`, which at least gets an — if broken —
// `__scope.` rewrite elsewhere in this package), so the bare reference here
// came out fully unresolved (`unresolvable-identifier` × 2, both
// `functions.canvasDotMetrics` and its globalScope-lifted duplicate). Keep
// these two literals in sync with the exported constants above if either
// tuning value ever changes.
// `period` is the RECURSION cycle of the zooming grid: the spacing folds
// back to `base` every ×period of zoom (octave fold), so a larger period
// means the grid re-tiles less often across a zoom gesture. `inkDivisor`
// sets the dot radius relative to spacing — larger divisor, smaller dots.
// Both default to the canvas-wide values so every existing board is
// byte-identical; per-board overrides thread through
// initPanzoomBoard({ dotPeriod, dotInkDivisor }) — the analytics map runs
// period 6 (2× longer recursion per Nika; the size halving landed
// globally via CANVAS_DOT_INK_DIVISOR above, so it takes no override).
export const canvasDotMetrics = (
  scale,
  base = 12,
  period = 3,
  inkDivisor = CANVAS_DOT_INK_DIVISOR
) => {
  const s = Number.isFinite(scale) && scale > 0 ? scale : 1
  const p = Number.isFinite(period) && period > 1 ? period : 3
  const octave = Math.pow(p, Math.floor(Math.log(s) / Math.log(p)))
  const folded = Number.isFinite(octave) && octave > 0 ? base * (s / octave) : base
  const spacing = Number.isFinite(folded) && folded > 0 ? folded : base
  // FA204 / frank trap — inlined literal (was a bare CANVAS_DOT_INK_DIVISOR
  // read), same reasoning as the base/period literals inlined above (see
  // this function's own header comment on frank's positional-default
  // resolver gap). Keep in sync with CANVAS_DOT_INK_DIVISOR above.
  const div = Number.isFinite(inkDivisor) && inkDivisor > 0 ? inkDivisor : 40
  return { spacing, radius: spacing / div }
}

// Stamp the metrics onto a dot-grid node for a camera. Size/position/radius are
// all theme-AGNOSTIC, so inline is safe here — the themed `backgroundImage`
// stays owned by CanvasDots' '@dark'/'@light' CSS and is never written inline
// (rule 1 at the top of this file). Returns the metrics it applied.
export const applyCanvasDotMetrics = (node, scale, offsetX = 0, offsetY = 0, base, opts = {}) => {
  if (!node || !node.style) return null
  // FA204 / frank trap — self-contained inline copy of canvasDotMetrics (a
  // bare sibling-export read is exactly as unresolvable post-frank as a
  // module-local read); the export above stays live for genuine external
  // consumers.
  const dotMetrics = (scale0, base0 = 12, period = 3, inkDivisor) => {
    const s = Number.isFinite(scale0) && scale0 > 0 ? scale0 : 1
    const p = Number.isFinite(period) && period > 1 ? period : 3
    const octave = Math.pow(p, Math.floor(Math.log(s) / Math.log(p)))
    const folded = Number.isFinite(octave) && octave > 0 ? base0 * (s / octave) : base0
    const spacing0 = Number.isFinite(folded) && folded > 0 ? folded : base0
    const div = Number.isFinite(inkDivisor) && inkDivisor > 0 ? inkDivisor : 40
    return { spacing: spacing0, radius: spacing0 / div }
  }
  const { spacing, radius: rawRadius } = dotMetrics(scale, base, opts.period, opts.inkDivisor)
  // Opt-in rendering-visibility floor (see CANVAS_DOT_VISIBILITY_FLOOR above)
  // — deliberately NOT applied by default, so every existing caller (and
  // canvasDotMetrics.test.js's locked unclamped values) stays byte-identical.
  // Only raises the radius; never lowers a healthy one.
  const radius =
    Number.isFinite(opts.minRadius) && opts.minRadius > 0
      ? Math.max(rawRadius, opts.minRadius)
      : rawRadius
  const px = ((offsetX % spacing) + spacing) % spacing
  const py = ((offsetY % spacing) + spacing) % spacing
  node.style.backgroundSize = `${spacing}px ${spacing}px`
  node.style.backgroundPosition = `${px}px ${py}px`
  // FA204 / frank trap — inlined literal (was a bare CANVAS_DOT_RADIUS_VAR
  // read); the export above stays live for genuine external consumers.
  node.style.setProperty('--canvas-dot-r', `${radius}px`)
  return { spacing, radius }
}

export const resolveCanvasBackgroundTheme = (theme, doc = globalThis.document) => {
  const key = String(theme || '').replace(/^@/, '')
  if (key === 'light' || key === 'dark') return key

  const attr = doc?.documentElement?.getAttribute?.('data-theme')
  if (attr === 'light' || attr === 'dark') return attr

  try {
    const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)')
    if (media) return media.matches ? 'dark' : 'light'
    return 'dark'
  } catch (_) {
    return 'dark'
  }
}

// FA204 / frank trap — every helper below (`_resolveTheme`/`_readCookieValue`
// etc.) is a self-contained local re-declaration inside each exported
// function that needs it, duplicated per caller — a bare sibling-export
// read (or a bare module-local read) is exactly as unresolvable post-frank
// as the other, and this chain (cookie name → theme → cookie value → sync →
// watch) would otherwise cascade the same trap through every link. The
// EXPORTS themselves stay live for genuine external consumers (grep
// confirms real callers of resolveCanvasBackgroundTheme/
// setCanvasBackgroundVar/clearCanvasBackgroundVar/
// syncCanvasBackgroundForTheme/watchCanvasBackgroundTheme outside this
// file).
export const canvasBackgroundCookieName = (theme, doc = globalThis.document) => {
  const resolveTheme = (t, d) => {
    const key = String(t || '').replace(/^@/, '')
    if (key === 'light' || key === 'dark') return key
    const attr = d?.documentElement?.getAttribute?.('data-theme')
    if (attr === 'light' || attr === 'dark') return attr
    try {
      const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)')
      if (media) return media.matches ? 'dark' : 'light'
      return 'dark'
    } catch (_) {
      return 'dark'
    }
  }
  return `canvas_${resolveTheme(theme, doc)}_background`
}

export const readCanvasBackgroundCookie = (theme, doc = globalThis.document, readCookie = null) => {
  const resolveTheme = (t, d) => {
    const key = String(t || '').replace(/^@/, '')
    if (key === 'light' || key === 'dark') return key
    const attr = d?.documentElement?.getAttribute?.('data-theme')
    if (attr === 'light' || attr === 'dark') return attr
    try {
      const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)')
      if (media) return media.matches ? 'dark' : 'light'
      return 'dark'
    } catch (_) {
      return 'dark'
    }
  }
  const cookieName = (t, d) => `canvas_${resolveTheme(t, d)}_background`
  const readCookieValue = (name, d) => {
    const cookie = d?.cookie || ''
    for (const part of String(cookie).split(';')) {
      const trimmed = part.trim()
      if (!trimmed) continue
      const eq = trimmed.indexOf('=')
      try {
        const rawName = eq < 0 ? trimmed : trimmed.slice(0, eq)
        if (decodeURIComponent(rawName) !== name) continue
        return decodeURIComponent(eq < 0 ? '' : trimmed.slice(eq + 1))
      } catch (err) {
        // Malformed percent-encoding in an unrelated cookie must not break
        // theme sync.
        console.warn('[canvasBackground] skipping malformed cookie:', err)
      }
    }
    return null
  }

  const name = cookieName(theme, doc)
  return typeof readCookie === 'function' ? readCookie(name) || null : readCookieValue(name, doc)
}

export const setCanvasBackgroundVar = (doc = globalThis.document, value) => {
  doc?.documentElement?.style?.setProperty?.('--canvas-bg', value)
}

export const clearCanvasBackgroundVar = (doc = globalThis.document) => {
  doc?.documentElement?.style?.removeProperty?.('--canvas-bg')
}

// Restore (or clear) the document-level `--canvas-bg` override from the
// active theme's cookie. Idempotent; safe to call on every theme flip.
export const syncCanvasBackgroundForTheme = (
  theme,
  doc = globalThis.document,
  readCookie = null
) => {
  const resolveTheme = (t, d) => {
    const key = String(t || '').replace(/^@/, '')
    if (key === 'light' || key === 'dark') return key
    const attr = d?.documentElement?.getAttribute?.('data-theme')
    if (attr === 'light' || attr === 'dark') return attr
    try {
      const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)')
      if (media) return media.matches ? 'dark' : 'light'
      return 'dark'
    } catch (_) {
      return 'dark'
    }
  }
  const cookieName = (t, d) => `canvas_${resolveTheme(t, d)}_background`
  const readCookieValue = (name, d) => {
    const cookie = d?.cookie || ''
    for (const part of String(cookie).split(';')) {
      const trimmed = part.trim()
      if (!trimmed) continue
      const eq = trimmed.indexOf('=')
      try {
        const rawName = eq < 0 ? trimmed : trimmed.slice(0, eq)
        if (decodeURIComponent(rawName) !== name) continue
        return decodeURIComponent(eq < 0 ? '' : trimmed.slice(eq + 1))
      } catch (err) {
        console.warn('[canvasBackground] skipping malformed cookie:', err)
      }
    }
    return null
  }
  const readCanvasBgCookie = (t, d, rc) => {
    const name = cookieName(t, d)
    return typeof rc === 'function' ? rc(name) || null : readCookieValue(name, d)
  }

  const value = readCanvasBgCookie(theme, doc, readCookie)
  if (value) doc?.documentElement?.style?.setProperty?.('--canvas-bg', value)
  else doc?.documentElement?.style?.removeProperty?.('--canvas-bg')
  return value || null
}

// Single document-level watcher that re-syncs the canvas-bg cookie var on
// EVERY `data-theme` flip — user-driven (changeGlobalTheme) AND OS-driven
// (scratch's prefers-color-scheme listener in 'auto' mode), so a light-mode
// pick never leaks into dark mode regardless of what flipped the theme.
// Singleton per document; lives for the app's lifetime by design.
export const watchCanvasBackgroundTheme = (doc = globalThis.document, readCookie = null) => {
  const root = doc?.documentElement
  if (!root) return null
  if (doc.__canvasBgThemeWatcher) return doc.__canvasBgThemeWatcher

  const Observer = doc.defaultView?.MutationObserver || globalThis.MutationObserver
  if (!Observer) return null

  const syncForTheme = (t, d, rc) => {
    const resolveTheme = (tt, dd) => {
      const key = String(tt || '').replace(/^@/, '')
      if (key === 'light' || key === 'dark') return key
      const attr = dd?.documentElement?.getAttribute?.('data-theme')
      if (attr === 'light' || attr === 'dark') return attr
      try {
        const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)')
        if (media) return media.matches ? 'dark' : 'light'
        return 'dark'
      } catch (_) {
        return 'dark'
      }
    }
    const cookieName = (tt, dd) => `canvas_${resolveTheme(tt, dd)}_background`
    const readCookieValue = (name, dd) => {
      const cookie = dd?.cookie || ''
      for (const part of String(cookie).split(';')) {
        const trimmed = part.trim()
        if (!trimmed) continue
        const eq = trimmed.indexOf('=')
        try {
          const rawName = eq < 0 ? trimmed : trimmed.slice(0, eq)
          if (decodeURIComponent(rawName) !== name) continue
          return decodeURIComponent(eq < 0 ? '' : trimmed.slice(eq + 1))
        } catch (err) {
          console.warn('[canvasBackground] skipping malformed cookie:', err)
        }
      }
      return null
    }
    const name = cookieName(t, d)
    const value = typeof rc === 'function' ? rc(name) || null : readCookieValue(name, d)
    if (value) d?.documentElement?.style?.setProperty?.('--canvas-bg', value)
    else d?.documentElement?.style?.removeProperty?.('--canvas-bg')
    return value || null
  }

  const observer = new Observer(() => {
    syncForTheme(root.getAttribute('data-theme'), doc, readCookie)
  })
  observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] })
  doc.__canvasBgThemeWatcher = observer
  return observer
}
